import { useEffect, useRef, useState, useCallback } from 'react';
import { Node, Edge } from '@xyflow/react';
import { NodeData } from '../initial-nodes';

interface AutoSaveStatus {
  isSaving: boolean;
  lastSaved?: Date;
  error?: Error;
}

export function useDiagramAutoSave(
  nodes: Node<NodeData>[],
  edges: Edge[],
  handleSave: (nodes: Node<NodeData>[], edges: Edge[], silent?: boolean) => Promise<{ success: boolean; error?: Error }>,
  isApplyingHistoryRef: React.MutableRefObject<boolean>,
  diagramLoaded: boolean,
  diagramId: string | undefined
): AutoSaveStatus & { markAsSaved: () => void } {
  const [status, setStatus] = useState<AutoSaveStatus>({ isSaving: false });
  const previousSavedStateRef = useRef<string | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const markAsSaved = useCallback(() => {
    const currentState = JSON.stringify({ nodes, edges });
    previousSavedStateRef.current = currentState;
    setStatus({
      isSaving: false,
      lastSaved: new Date(),
      error: undefined
    });
  }, [nodes, edges]);

  useEffect(() => {
    // Não salvar se não houver ID do diagrama
    if (!diagramId) return;
    
    // Não salvar se o diagrama ainda não foi carregado
    if (!diagramLoaded) return;

    // Não salvar se estiver aplicando undo/redo
    if (isApplyingHistoryRef.current) return;

    // Limpar timeout anterior
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Criar debounce de 3 segundos
    saveTimeoutRef.current = setTimeout(async () => {
      // Serializar estado atual
      const currentState = JSON.stringify({ nodes, edges });

      // Se o estado não mudou desde o último salvamento, não salvar
      if (previousSavedStateRef.current === currentState) {
        return;
      }

      // Iniciar salvamento
      setStatus({ isSaving: true });

      try {
        const result = await handleSave(nodes, edges, true); // silent = true

        if (result.success) {
          // Atualizar último estado salvo
          previousSavedStateRef.current = currentState;
          setStatus({
            isSaving: false,
            lastSaved: new Date(),
            error: undefined
          });
        } else {
          setStatus({
            isSaving: false,
            error: result.error || new Error('Erro desconhecido ao salvar')
          });
        }
      } catch (error) {
        setStatus({
          isSaving: false,
          error: error instanceof Error ? error : new Error('Erro ao salvar diagrama')
        });
      }
    }, 3000);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [nodes, edges, handleSave, isApplyingHistoryRef, diagramLoaded, diagramId]);

  return { ...status, markAsSaved };
}
